import { useCallback, useEffect, useState } from 'react';

import { FLOATING_PANEL_COLLAPSED_WIDTH } from './FloatingPanel';

export type CombinedPanelTab = 'outliner' | 'detail';
type PanelLayoutMode = 'split' | 'combined';

const OUTLINER_MIN_WIDTH = 260;
const INSPECTOR_MIN_WIDTH = 280;
const DEFAULT_OUTLINER_WIDTH = 300;
const DEFAULT_INSPECTOR_WIDTH = 336;
const DEFAULT_OUTLINER_HEIGHT = 560;
const DEFAULT_INSPECTOR_HEIGHT = 560;
const PANEL_MARGIN_PX = 12;
const OUTLINER_CONTENT_WRAPPER_PADDING_X = 16;
const COMBINED_LAYOUT_ENTRY_MULTIPLIER = 1.45;
const SPLIT_LAYOUT_EXIT_BUFFER_PX = 32;
const TOOLBAR_TRIGGER_HIDE_GAP_PX = 170;
const TOOLBAR_TRIGGER_SHOW_GAP_PX = 190;
const COMBINED_TOOLBAR_RESERVED_WIDTH_PX = 120;

function resolveToolbarTriggerLabelVisibility(
  previousVisible: boolean,
  gapPx: number,
): boolean {
  if (previousVisible) {
    return gapPx > TOOLBAR_TRIGGER_HIDE_GAP_PX;
  }

  return gapPx > TOOLBAR_TRIGGER_SHOW_GAP_PX;
}

interface UseWorkspaceLayoutOptions {
  hasRenderableSkeleton: boolean;
  showSidePanels: boolean;
}

export default function useWorkspaceLayout({
  hasRenderableSkeleton,
  showSidePanels,
}: UseWorkspaceLayoutOptions) {
  const [isOutlinerOpen, setIsOutlinerOpen] = useState(true);
  const [isInspectorOpen, setIsInspectorOpen] = useState(true);
  const [outlinerWidth, setOutlinerWidth] = useState(DEFAULT_OUTLINER_WIDTH);
  const [outlinerHeight, setOutlinerHeight] = useState(DEFAULT_OUTLINER_HEIGHT);
  const [inspectorWidth, setInspectorWidth] = useState(DEFAULT_INSPECTOR_WIDTH);
  const [inspectorHeight, setInspectorHeight] = useState(
    DEFAULT_INSPECTOR_HEIGHT,
  );
  const [outlinerMinContentWidth, setOutlinerMinContentWidth] = useState(0);
  const [viewportWidth, setViewportWidth] = useState(() =>
    typeof window === 'undefined' ? 1440 : window.innerWidth,
  );
  const [panelLayoutMode, setPanelLayoutMode] =
    useState<PanelLayoutMode>('split');
  const [activeCombinedTab, setActiveCombinedTab] =
    useState<CombinedPanelTab>('outliner');
  const [combinedPanelWidth, setCombinedPanelWidth] = useState(() =>
    Math.max(DEFAULT_OUTLINER_WIDTH, DEFAULT_INSPECTOR_WIDTH),
  );
  const [combinedPanelHeight, setCombinedPanelHeight] = useState(() =>
    Math.max(DEFAULT_OUTLINER_HEIGHT, DEFAULT_INSPECTOR_HEIGHT),
  );
  const [isCombinedPanelOpen, setIsCombinedPanelOpen] = useState(true);
  const [showToolbarTriggerLabels, setShowToolbarTriggerLabels] =
    useState(true);

  const outlinerMinWidth = Math.max(
    OUTLINER_MIN_WIDTH,
    outlinerMinContentWidth + OUTLINER_CONTENT_WRAPPER_PADDING_X,
  );
  const panelGapPx = PANEL_MARGIN_PX;
  const overlayHorizontalInsetPx = PANEL_MARGIN_PX * 2;
  const inspectorMinEffectiveWidth = isInspectorOpen
    ? INSPECTOR_MIN_WIDTH
    : FLOATING_PANEL_COLLAPSED_WIDTH;
  const outlinerMinEffectiveWidth = isOutlinerOpen
    ? outlinerMinWidth
    : FLOATING_PANEL_COLLAPSED_WIDTH;
  const availablePanelSpan = Math.max(0, viewportWidth - overlayHorizontalInsetPx);
  const splitHardMinWidth = outlinerMinWidth + INSPECTOR_MIN_WIDTH + panelGapPx;
  const combinedLayoutEnterThreshold =
    splitHardMinWidth * COMBINED_LAYOUT_ENTRY_MULTIPLIER;
  const shouldUseCombinedForWidth =
    availablePanelSpan < combinedLayoutEnterThreshold;
  const splitCanRestoreFromCombined =
    availablePanelSpan >=
    combinedLayoutEnterThreshold + SPLIT_LAYOUT_EXIT_BUFFER_PX;
  const usesCombinedLayout =
    hasRenderableSkeleton && panelLayoutMode === 'combined';
  const isOutlinerPanelInteractable =
    showSidePanels &&
    (usesCombinedLayout
      ? isCombinedPanelOpen && activeCombinedTab === 'outliner'
      : isOutlinerOpen);
  const combinedPanelMinWidth =
    activeCombinedTab === 'outliner' ? outlinerMinWidth : INSPECTOR_MIN_WIDTH;
  const combinedPanelMaxWidth = Math.max(
    combinedPanelMinWidth,
    availablePanelSpan,
  );
  const combinedPanelResolvedWidth = Math.max(
    combinedPanelMinWidth,
    Math.min(combinedPanelWidth, combinedPanelMaxWidth),
  );
  const combinedPanelOccupiedWidth = isCombinedPanelOpen
    ? combinedPanelResolvedWidth
    : FLOATING_PANEL_COLLAPSED_WIDTH;
  const outlinerMaxWidth = Math.max(
    outlinerMinWidth,
    availablePanelSpan - panelGapPx - inspectorMinEffectiveWidth,
  );
  const inspectorMaxWidth = Math.max(
    INSPECTOR_MIN_WIDTH,
    availablePanelSpan - panelGapPx - outlinerMinEffectiveWidth,
  );
  const outlinerPanelWidth = hasRenderableSkeleton
    ? usesCombinedLayout
      ? combinedPanelOccupiedWidth
      : isOutlinerOpen
        ? outlinerWidth
        : FLOATING_PANEL_COLLAPSED_WIDTH
    : 0;
  const inspectorPanelWidth = hasRenderableSkeleton
    ? usesCombinedLayout
      ? 0
      : isInspectorOpen
        ? inspectorWidth
        : FLOATING_PANEL_COLLAPSED_WIDTH
    : 0;
  const toolbarHalfOverlayWidthPx = availablePanelSpan * 0.5;
  const leftToolbarGapPx = toolbarHalfOverlayWidthPx - outlinerPanelWidth;
  const rightToolbarGapPx = toolbarHalfOverlayWidthPx - inspectorPanelWidth;
  const nearestToolbarGapPx = Math.min(leftToolbarGapPx, rightToolbarGapPx);
  const combinedToolbarGapPx = Math.max(
    0,
    availablePanelSpan -
      combinedPanelOccupiedWidth -
      panelGapPx -
      COMBINED_TOOLBAR_RESERVED_WIDTH_PX,
  );
  const toolbarLabelGapPx = usesCombinedLayout
    ? combinedToolbarGapPx
    : nearestToolbarGapPx;

  const getCombinedMinWidthForTab = useCallback(
    (tab: CombinedPanelTab) =>
      tab === 'outliner' ? outlinerMinWidth : INSPECTOR_MIN_WIDTH,
    [outlinerMinWidth],
  );

  useEffect(() => {
    setShowToolbarTriggerLabels((previousVisible) =>
      resolveToolbarTriggerLabelVisibility(previousVisible, toolbarLabelGapPx),
    );
  }, [toolbarLabelGapPx]);

  useEffect(() => {
    if (!hasRenderableSkeleton) {
      if (panelLayoutMode !== 'split') {
        setPanelLayoutMode('split');
      }
      return;
    }

    if (panelLayoutMode === 'split') {
      if (isOutlinerOpen && isInspectorOpen && shouldUseCombinedForWidth) {
        const nextTab: CombinedPanelTab = 'outliner';
        const nextCombinedMinWidth = getCombinedMinWidthForTab(nextTab);
        setPanelLayoutMode('combined');
        setIsCombinedPanelOpen(true);
        setCombinedPanelWidth(nextCombinedMinWidth);
        setCombinedPanelHeight((previousHeight) =>
          Math.max(previousHeight, outlinerHeight, inspectorHeight),
        );
        setActiveCombinedTab(nextTab);
      }
      return;
    }

    if (splitCanRestoreFromCombined) {
      setPanelLayoutMode('split');
      setIsOutlinerOpen(true);
      setIsInspectorOpen(true);
      setOutlinerWidth(outlinerMinWidth);
      setInspectorWidth(INSPECTOR_MIN_WIDTH);
    }
  }, [
    getCombinedMinWidthForTab,
    hasRenderableSkeleton,
    inspectorHeight,
    isInspectorOpen,
    isOutlinerOpen,
    outlinerHeight,
    outlinerMinWidth,
    panelLayoutMode,
    shouldUseCombinedForWidth,
    splitCanRestoreFromCombined,
  ]);

  useEffect(() => {
    const handleResize = () => setViewportWidth(window.innerWidth);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  const handleOutlinerMinContentWidthChange = useCallback((nextWidth: number) => {
    setOutlinerMinContentWidth((previousWidth) =>
      Math.abs(previousWidth - nextWidth) < 0.5 ? previousWidth : nextWidth,
    );
  }, []);

  const handleOutlinerWidthChange = useCallback(
    (nextWidth: number) => {
      if (usesCombinedLayout) return;

      const maxWidthForOutliner = Math.max(
        outlinerMinWidth,
        availablePanelSpan - panelGapPx - inspectorMinEffectiveWidth,
      );
      const clampedOutlinerWidth = Math.max(
        outlinerMinWidth,
        Math.min(nextWidth, maxWidthForOutliner),
      );

      if (isInspectorOpen) {
        const maxWidthForInspectorAfterOutlinerResize = Math.max(
          INSPECTOR_MIN_WIDTH,
          availablePanelSpan - panelGapPx - clampedOutlinerWidth,
        );
        setInspectorWidth((previousWidth) =>
          Math.min(previousWidth, maxWidthForInspectorAfterOutlinerResize),
        );
      }

      setOutlinerWidth(clampedOutlinerWidth);
    },
    [
      availablePanelSpan,
      inspectorMinEffectiveWidth,
      isInspectorOpen,
      outlinerMinWidth,
      panelGapPx,
      usesCombinedLayout,
    ],
  );

  const handleInspectorWidthChange = useCallback(
    (nextWidth: number) => {
      if (usesCombinedLayout) return;

      const maxWidthForInspector = Math.max(
        INSPECTOR_MIN_WIDTH,
        availablePanelSpan - panelGapPx - outlinerMinEffectiveWidth,
      );
      const clampedInspectorWidth = Math.max(
        INSPECTOR_MIN_WIDTH,
        Math.min(nextWidth, maxWidthForInspector),
      );

      if (isOutlinerOpen) {
        const maxWidthForOutlinerAfterInspectorResize = Math.max(
          outlinerMinWidth,
          availablePanelSpan - panelGapPx - clampedInspectorWidth,
        );
        setOutlinerWidth((previousWidth) =>
          Math.min(previousWidth, maxWidthForOutlinerAfterInspectorResize),
        );
      }

      setInspectorWidth(clampedInspectorWidth);
    },
    [
      availablePanelSpan,
      isOutlinerOpen,
      outlinerMinEffectiveWidth,
      outlinerMinWidth,
      panelGapPx,
      usesCombinedLayout,
    ],
  );

  const handleCombinedPanelWidthChange = useCallback(
    (nextWidth: number) => {
      const clampedWidth = Math.max(
        combinedPanelMinWidth,
        Math.min(nextWidth, combinedPanelMaxWidth),
      );
      setCombinedPanelWidth((previousWidth) =>
        Math.abs(previousWidth - clampedWidth) < 0.5
          ? previousWidth
          : clampedWidth,
      );
    },
    [combinedPanelMaxWidth, combinedPanelMinWidth],
  );

  const handleCombinedTabSelect = useCallback(
    (nextTab: CombinedPanelTab) => {
      const nextMinWidth = getCombinedMinWidthForTab(nextTab);
      setActiveCombinedTab(nextTab);
      setCombinedPanelWidth(nextMinWidth);
    },
    [getCombinedMinWidthForTab],
  );

  return {
    activeCombinedTab,
    availablePanelSpan,
    combinedPanelHeight,
    combinedPanelMaxWidth,
    combinedPanelMinWidth,
    combinedPanelResolvedWidth,
    getCombinedMinWidthForTab,
    handleCombinedPanelWidthChange,
    handleCombinedTabSelect,
    handleInspectorWidthChange,
    handleOutlinerMinContentWidthChange,
    handleOutlinerWidthChange,
    inspectorHeight,
    inspectorMaxWidth,
    inspectorWidth,
    isCombinedPanelOpen,
    isOutlinerOpen,
    isOutlinerPanelInteractable,
    isInspectorOpen,
    outlinerHeight,
    outlinerMaxWidth,
    outlinerMinWidth,
    outlinerWidth,
    setActiveCombinedTab,
    setCombinedPanelHeight,
    setCombinedPanelWidth,
    setIsCombinedPanelOpen,
    setIsOutlinerOpen,
    setInspectorHeight,
    setIsInspectorOpen,
    setOutlinerHeight,
    showToolbarTriggerLabels,
    usesCombinedLayout,
    viewportWidth,
  };
}
